import { createAsyncThunk, createSelector, createSlice, } from "@reduxjs/toolkit";
import { getPlayers } from "../../api/players/getPlayers";
import { evolutionPathParam } from "../../evolutions/evolutionPath";
import { getCoinValue, } from "../../types/player";
/**
 * Identity of a compared player. `resourceId` alone isn't enough — a base
 * card and its evolved versions share one, and the evolution builder can
 * produce several versions of the same player, all comparable side by side.
 * Undefined parts contribute nothing, so a base card's key is just its
 * resourceId; the path resolves like the comparison fetch input
 * (`evolutionData` first).
 */
export const comparePlayerKey = (player) => [
    player.resourceId,
    player.evoPathId ?? "",
    evolutionPathParam(player.evolutionData?.evolutionPath ?? player.evolutionPath ?? [], player.evolutionData?.pathwayPath ?? player.pathwayPath),
].join("|");
const initialState = {
    slots: [],
    context: null,
    suggestedPlayers: [],
    suggestionsKey: null,
    suggestionsQuery: null,
    suggestionsLoading: false,
    suggestionsRequestId: null,
};
/**
 * What a fresh slot is viewed at: zero chem, basic chemstyle. Exported so the
 * comparison fetch can grade URL-seeded players identically to the slots
 * they'll become (avoids a key mismatch → duplicate refetch after seeding).
 */
export const DEFAULT_SLOT_CHEM = {
    chemistry: 0,
    chemstyleId: 250,
};
const defaultSlot = (player) => ({
    player,
    ...DEFAULT_SLOT_CHEM,
});
/**
 * The player's best chemstyle at a chemistry level, off their meta-ratings
 * list. Staging sites often know the chemistry but not the style (search /
 * recommendation cards only resolve one when dropped on the pitch) — the
 * shown meta rating is the best style's, so stage that style.
 */
const bestChemstyleAtChem = (player, chemistry) => player.metaRatings?.find((m) => m.chemistry === chemistry && m.isBestChemstyleAtChem)?.chemstyleId;
/**
 * Role suggestions are graded under: the selected role, else the first
 * compared player's best role (`playerRoleId` on the card is the backend's
 * best role for the player). No role at all while nothing is compared —
 * the suggestions are then just the overall best players.
 */
const suggestionsRoleId = (players, playerRoleId) => players.length > 0 ? (playerRoleId ?? players[0]?.playerRoleId) : undefined;
/**
 * Identity of a suggestions fetch — the compared set (order-independent) plus
 * the graded role (selected, or the first player's best) — to know when the
 * stored suggestions are stale.
 */
const suggestionsKeyFor = (players, playerRoleId) => `${suggestionsRoleId(players, playerRoleId) ?? "best"}|${players
    .map((player) => player.resourceId)
    .sort((a, b) => a - b)
    .join(",")}`;
/**
 * Players to suggest in the add-search view: at the price of the most
 * expensive compared player or cheaper (no price cap when no compared player
 * has a resolved coin price), best meta-rating first. Graded under the
 * selected role when one is chosen, otherwise the first compared player's
 * best role (same `player-role-id` filter as the Players DB). With nothing
 * compared, no filters at all — the overall best players. Skipped while the
 * stored suggestions already match the current compared set + role, so
 * opening the view is a cache hit until either changes.
 */
export const fetchSuggestedComparePlayers = createAsyncThunk("compare-players/fetch-suggestions", async ({ playerRoleId }, { getState }) => {
    const players = getState().comparePlayersReducer.slots.map((slot) => slot.player);
    const maxPrice = Math.max(0, ...players.map((player) => getCoinValue(player.priceInfo)));
    const { players: suggested } = await getPlayers({
        page: 1,
        sort: "meta-rating",
        playerRoleId: suggestionsRoleId(players, playerRoleId),
        priceMax: maxPrice > 0 ? maxPrice : undefined,
    });
    const comparedIds = new Set(players.map((player) => player.resourceId));
    return {
        key: suggestionsKeyFor(players, playerRoleId),
        players: suggested.filter((player) => !comparedIds.has(player.resourceId)),
        query: {
            maxPrice: maxPrice > 0 ? maxPrice : null,
            playerRoleId: playerRoleId ?? null,
        },
    };
}, {
    condition: ({ playerRoleId }, { getState }) => {
        const { slots, suggestionsKey } = getState()
            .comparePlayersReducer;
        return (suggestionsKeyFor(slots.map((slot) => slot.player), playerRoleId) !== suggestionsKey);
    },
});
const comparePlayerSlice = createSlice({
    name: "comparePlayers",
    initialState,
    reducers: {
        // Swaps the players (the silent comparison refetch's detailed cards) while
        // each slot keeps its saved chemistry/chemstyleId. The caller re-attaches
        // the evo identity fields to the response players, so key matching holds.
        setComparePlayers: (state, action) => {
            state.slots = action.payload.map((player) => {
                const existing = state.slots.find((slot) => comparePlayerKey(slot.player) === comparePlayerKey(player));
                return existing ? { ...existing, player } : defaultSlot(player);
            });
        },
        // Optional second arg: the chemistry/chemstyle context of the staging site
        // (a squad-builder slot's applied chem, the Players DB's chem sort), so
        // the slot opens at what the user was looking at instead of the default.
        addComparePlayer: {
            reducer: (state, action) => {
                const { player, chemistry, chemstyleId } = action.payload;
                if (!state.slots.some((slot) => comparePlayerKey(slot.player) === comparePlayerKey(player))) {
                    // A chemstyle only means something above 0 chem: there the site's
                    // explicit style wins, else the player's best at that chemistry
                    // (what their shown meta rating already assumes). At 0 chem (or no
                    // chem context) the slot keeps the Basic default, whatever the
                    // staging site sent.
                    const resolvedChemstyleId = chemistry
                        ? (chemstyleId ?? bestChemstyleAtChem(player, chemistry))
                        : undefined;
                    state.slots.push({
                        ...defaultSlot(player),
                        ...(chemistry != null && { chemistry }),
                        ...(resolvedChemstyleId != null && {
                            chemstyleId: resolvedChemstyleId,
                        }),
                    });
                }
            },
            prepare: (player, chem) => ({
                payload: {
                    player,
                    chemistry: chem?.chemistry,
                    chemstyleId: chem?.chemstyleId,
                },
            }),
        },
        setCompareContext: (state, action) => {
            state.context = action.payload;
        },
        setCompareChemistry: (state, action) => {
            const slot = state.slots.find((s) => comparePlayerKey(s.player) === action.payload.playerKey);
            if (slot)
                slot.chemistry = action.payload.chemistry;
        },
        // On-chem slots (chemistry > 0) follow a newly graded role: the best
        // chemstyle is role-dependent, so re-derive each from the player's meta
        // ratings at the slot's chemistry. `playerRoleId` undefined = "Best Role"
        // — each player's own best role. Slots without a matching entry (or at 0
        // chem, where no style applies) keep what they have.
        applyBestChemstylesForRole: (state, action) => {
            for (const slot of state.slots) {
                if (!slot.chemistry)
                    continue;
                const roleId = action.payload.playerRoleId ?? slot.player.playerRoleId;
                const best = slot.player.metaRatings?.find((m) => m.chemistry === slot.chemistry &&
                    m.isBestChemstyleAtChem &&
                    (m.playerRoleId == null || m.playerRoleId === roleId))?.chemstyleId;
                if (best != null)
                    slot.chemstyleId = best;
            }
        },
        setCompareChemStyleId: (state, action) => {
            const slot = state.slots.find((s) => comparePlayerKey(s.player) === action.payload.playerKey);
            if (slot)
                slot.chemstyleId = action.payload.chemstyleId;
        },
        removeComparePlayer: (state, action) => {
            const removedKey = comparePlayerKey(action.payload);
            state.slots = state.slots.filter((slot) => comparePlayerKey(slot.player) !== removedKey);
            // Empty set = comparison over; a later staging shouldn't inherit an old
            // trigger site's role.
            if (state.slots.length === 0)
                state.context = null;
        },
        clearComparePlayers: (state) => {
            state.slots = [];
            state.context = null;
            state.suggestedPlayers = [];
            state.suggestionsKey = null;
            state.suggestionsQuery = null;
            state.suggestionsLoading = false;
            // In-flight fetches must not land after the clear.
            state.suggestionsRequestId = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSuggestedComparePlayers.pending, (state, action) => {
            state.suggestionsRequestId = action.meta.requestId;
            state.suggestionsLoading = true;
        });
        builder.addCase(fetchSuggestedComparePlayers.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.suggestionsRequestId)
                return;
            state.suggestedPlayers = action.payload.players;
            state.suggestionsKey = action.payload.key;
            state.suggestionsQuery = action.payload.query;
            state.suggestionsLoading = false;
        });
        builder.addCase(fetchSuggestedComparePlayers.rejected, (state, action) => {
            if (action.meta.requestId !== state.suggestionsRequestId)
                return;
            state.suggestionsLoading = false;
        });
    },
});
export const { setComparePlayers, addComparePlayer, setCompareContext, setCompareChemistry, setCompareChemStyleId, applyBestChemstylesForRole, removeComparePlayer, clearComparePlayers, } = comparePlayerSlice.actions;
export const comparePlayerSlotsSelector = (state) => state.comparePlayersReducer.slots;
/** The compared players alone, memoized so consumers can keep depending on a stable array. */
export const comparePlayersSelector = createSelector([comparePlayerSlotsSelector], (slots) => slots.map((slot) => slot.player));
/**
 * Whether this player is currently staged for comparison — what every
 * "Compare +" toggle renders its active state from.
 *
 * Matched on the full `comparePlayerKey`, not the resourceId: a base card and
 * its evolved versions share a resourceId but stage as separate columns, so
 * keying on the id alone would show the base card as staged the moment an evo
 * of it was added. Takes the identity rather than a whole player so callers
 * that only have the id parts (the DetailedPlayer toolbar builds one from the
 * page's evolution state) can use it too; `undefined` — a player that hasn't
 * loaded yet — is simply not staged.
 */
export const isInCompareSelector = (player) => (state) => {
    if (!player)
        return false;
    const key = comparePlayerKey(player);
    return state.comparePlayersReducer.slots.some((slot) => comparePlayerKey(slot.player) === key);
};
export const compareContextSelector = (state) => state.comparePlayersReducer.context;
export const suggestedComparePlayersSelector = (state) => state.comparePlayersReducer.suggestedPlayers;
export const suggestionsQuerySelector = (state) => state.comparePlayersReducer.suggestionsQuery;
export const suggestionsLoadingSelector = (state) => state.comparePlayersReducer.suggestionsLoading;
export const comparePlayersReducer = comparePlayerSlice.reducer;
//# sourceMappingURL=comparePlayerSlice.js.map